/**
 * TaskDetailsTabs Component
 *
 * Renders the tab navigation and content for task details:
 * - Comments tab (description + user comments)
 * - Campaigns tab (related campaigns display)
 * - History tab (activity timeline)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Tabs, Tag } from 'antd';
import Text from 'antd/lib/typography/Text';
import { TaskComments } from '../TaskComments';
import { Histories } from '../../../../shared/Histories';
import { hasCampaignsFeature } from '../../utils/task.utils';
import { space, fontSize, radius, text, border, link, bg, neutral, shadow, duration } from '../../configs/designTokens';

const { TabPane } = Tabs;

const TaskDetailsTabs = ({
  currentTask,
  currentTaskId,
  currentUser,
  activeTab,
  onTabChange,
  histories,
  loadingHistories,
  windowWidth,
}) => {
  // Get comment count (only user comments, not the initial task description)
  const commentCount = currentTask?.attachedComments?.length || 0;
  const historyCount = histories?.length || 0;

  return (
    <Col style={{ width: '100%', flex: windowWidth < 1200 ? 'none' : 1 }}>
      <Row
        style={{
          backgroundColor: bg.card,
          border: 'none',
          paddingTop: '2vh',
          paddingBottom: '1vh',
        }}
      >
        <Tabs
          className="task-tabs-details-history"
          activeKey={activeTab}
          onChange={onTabChange}
          type="card"
          tabBarStyle={{
            border: `4px solid ${border.slateLight}`,
            borderRadius: radius.md,
            width: 'fit-content',
            display: 'inline-flex',
            gap: space[0],
            margin: space[0],
          }}
        >
          <TabPane tab={`Comments (${commentCount})`} key="1" />
          {currentTask && currentTask.relatedCampaigns && currentTask.relatedCampaigns.length > 0 && hasCampaignsFeature(currentUser) && (
            <TabPane tab="Campaigns" key="2" />
          )}
          <TabPane tab={`History (${historyCount})`} key="3" />
        </Tabs>
      </Row>

      {/* Details Tab */}
      {activeTab === '1' && (
        <Row style={{ justifyContent: 'center' }}>
          <Col
            style={{
              width: '100%',
              marginRight: space[5],
              paddingBottom: windowWidth < 1200 ? space[5] : '20%',
            }}
          >
            <TaskComments currentTask={currentTask} currentTaskId={currentTaskId} />
          </Col>
        </Row>
      )}

      {/* Campaigns Tab */}
      {activeTab === '2' && (
        <Row style={{ justifyContent: 'center' }}>
          <Col
            style={{
              width: '100%',
              marginRight: space[5],
              paddingBottom: '20%',
            }}
          >
            <Card
              title={
                <Text strong style={{ fontSize: fontSize.title }}>
                  Related Campaigns
                </Text>
              }
              style={{ borderRadius: radius.md, marginTop: space[5] }}
            >
              {currentTask && currentTask.relatedCampaigns && currentTask.relatedCampaigns.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: windowWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: space[4],
                  }}
                >
                  {currentTask.relatedCampaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      style={{
                        backgroundColor: neutral[50],
                        border: `1px solid ${border.slate}`,
                        borderRadius: radius.md,
                        padding: space[4],
                        transition: `all ${duration.base}`,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = link.blueBg;
                        e.currentTarget.style.borderColor = link.blue;
                        e.currentTarget.style.boxShadow = shadow.blueSm;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = neutral[50];
                        e.currentTarget.style.borderColor = border.slate;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      onClick={() => window.open(`/campaigns/details/${campaign.id}`, '_blank')}
                    >
                      <div style={{ marginBottom: space[3] }}>
                        <Text
                          strong
                          style={{
                            fontSize: fontSize.subhead,
                            color: text.dark,
                            display: 'block',
                            marginBottom: space[2],
                          }}
                        >
                          {campaign.name}
                        </Text>
                        {campaign.status && (
                          <Tag color="blue" style={{ marginBottom: space[2] }}>
                            {campaign.status}
                          </Tag>
                        )}
                      </div>
                      <div style={{ fontSize: fontSize.meta, color: text.secondary }}>
                        {campaign.startDate && campaign.endDate && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: space[1.5] }}>
                            <span>📅</span>
                            <span>
                              {new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* History Tab */}
      {activeTab === '3' && (
        <Row>
          <Col style={{ width: windowWidth < 1200 ? '100%' : '48vw', marginRight: windowWidth < 1200 ? '0' : space[5] }}>
            <Histories histories={histories} loading={loadingHistories} />
          </Col>
        </Row>
      )}
    </Col>
  );
};

TaskDetailsTabs.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    relatedCampaigns: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      status: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    })),
  }),
  currentTaskId: PropTypes.string.isRequired,
  currentUser: PropTypes.object,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  histories: PropTypes.array,
  loadingHistories: PropTypes.bool,
  windowWidth: PropTypes.number.isRequired,
};

TaskDetailsTabs.defaultProps = {
  currentTask: null,
  currentUser: null,
  histories: [],
  loadingHistories: false,
};

export default TaskDetailsTabs;
